import { outLogin } from '@/services/ant-design-pro/api';
import { adminLogout } from '@/services/admin/api';
import { LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import { Spin } from 'antd';
import type { MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu, children }) => {
  const { initialState, setInitialState } = useModel('@@initialState');

  /**
   * 智能退出登录
   * 根据当前用户类型和Token存在情况选择合适的登出方式
   */
  const loginOut = async () => {
    try {
      // 检查用户类型和Token存在情况
      const adminToken = localStorage.getItem('admin_token');
      const userToken = localStorage.getItem('token');
      const { currentUser } = initialState || {};

      // 判断是否为管理员用户
      const isAdmin =
        currentUser?.access === '超级管理员' || currentUser?.access === '普通管理员' || adminToken;

      // 调用相应的登出API
      if (isAdmin && adminToken) {
        console.log('执行管理员登出...');
        await adminLogout();
        // 清除管理员Token
        localStorage.removeItem('admin_token');
      } else if (userToken) {
        console.log('执行普通用户登出...');
        await outLogin();
        // 清除普通用户Token
        localStorage.removeItem('token');
      } else {
        console.log('未找到有效Token，直接清除本地状态');
      }

      // 无论何种情况都清除所有本地存储
      localStorage.removeItem('admin_token');
      localStorage.removeItem('token');

      console.log('登出成功，准备跳转到登录页面');
    } catch (error) {
      console.error('登出过程中发生错误:', error);
      // 即使API调用失败，也要清除本地Token
      localStorage.removeItem('admin_token');
      localStorage.removeItem('token');
    }

    // 处理页面跳转
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });

    const redirect = urlParams.get('redirect');
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
